import React, { useState, useEffect } from 'react';
import { 
  Notifications, 
  Warning, 
  Error, 
  Info, 
  CheckCircle, 
  PriorityHigh, 
  Schedule, 
  Person, 
  Business, 
  AttachMoney,
  TrendingUp,
  TrendingDown,
  FilterList,
  Search,
  MoreVert,
  MarkEmailRead,
  Delete,
  Archive
} from '@mui/icons-material';

const AlertsDashboard = () => {
  const [alerts, setAlerts] = useState([]);
  const [selectedAlerts, setSelectedAlerts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    // Mock data for RM360 alerts
    const mockAlerts = [
      {
        id: 1,
        type: 'critical',
        title: 'Portfolio Risk Alert - Khaled Al-Maani',
        description: 'Customer portfolio has exceeded risk tolerance threshold. Immediate review required.',
        customer: 'Khaled Al-Maani',
        customerId: '101136',
        customerSegment: 'Premium',
        timestamp: '30 minutes ago',
        isRead: false,
        priority: 'critical',
        category: 'portfolio',
        actionRequired: 'Schedule risk review meeting',
        impact: 'High',
        value: 45000
      },
      {
        id: 2,
        type: 'warning',
        title: 'Investment Opportunity - Nader Al-Masri',
        description: 'New high-yield investment opportunity available for customer. Time-sensitive decision required.',
        customer: 'Nader Al-Masri',
        customerId: '101982',
        customerSegment: 'Priority',
        timestamp: '2 hours ago',
        isRead: false,
        priority: 'high',
        category: 'investment',
        actionRequired: 'Contact customer within 24 hours',
        impact: 'Medium',
        value: 120000
      },
      {
        id: 3,
        type: 'info',
        title: 'Credit Limit Increase Request - Amira Al-Kurdi',
        description: 'Customer has requested credit limit increase. Application requires RM approval.',
        customer: 'Amira Al-Kurdi',
        customerId: '103926',
        customerSegment: 'General Mass Market',
        timestamp: '4 hours ago',
        isRead: true,
        priority: 'medium',
        category: 'credit',
        actionRequired: 'Review application and approve/deny',
        impact: 'Low',
        value: 65000
      },
      {
        id: 4,
        type: 'warning',
        title: 'Insurance Renewal Due - Mahmoud Al-Jundi',
        description: 'Customer insurance policy expires in 7 days. Renewal discussion needed.',
        customer: 'Mahmoud Al-Jundi',
        customerId: '102874',
        customerSegment: 'Premium',
        timestamp: '6 hours ago',
        isRead: true,
        priority: 'medium',
        category: 'insurance',
        actionRequired: 'Contact customer for renewal discussion',
        impact: 'Medium',
        value: 85000
      },
      {
        id: 5,
        type: 'success',
        title: 'Investment Goal Achieved - Yousef Al-Zoubi',
        description: 'Customer has reached their investment target. Portfolio rebalancing recommended.',
        customer: 'Yousef Al-Zoubi',
        customerId: '101915',
        customerSegment: 'Premium',
        timestamp: '1 day ago',
        isRead: true,
        priority: 'low',
        category: 'portfolio',
        actionRequired: 'Schedule portfolio review meeting',
        impact: 'Low',
        value: 95000
      },
      {
        id: 6,
        type: 'critical',
        title: 'Account Suspension Risk - Saja Al-Dabbas',
        description: 'Customer account showing suspicious activity. Investigation required.',
        customer: 'Saja Al-Dabbas',
        customerId: '104567',
        customerSegment: 'Priority',
        timestamp: '1 day ago',
        isRead: false,
        priority: 'critical',
        category: 'security',
        actionRequired: 'Immediate account review and customer contact',
        impact: 'High',
        value: 75000
      },
      {
        id: 7,
        type: 'info',
        title: 'New Product Launch - Mona Ibrahim',
        description: 'Customer eligible for new premium investment product. Introduction opportunity.',
        customer: 'Mona Ibrahim',
        customerId: '104789',
        customerSegment: 'Employee Banking',
        timestamp: '2 days ago',
        isRead: true,
        priority: 'low',
        category: 'product',
        actionRequired: 'Schedule product introduction call',
        impact: 'Low',
        value: 45000
      },
      {
        id: 8,
        type: 'warning',
        title: 'Meeting Reminder - Leen Abu Zaid',
        description: 'Portfolio review meeting scheduled for tomorrow. Preparation required.',
        customer: 'Leen Abu Zaid',
        customerId: '104890',
        customerSegment: 'Priority',
        timestamp: '2 days ago',
        isRead: true,
        priority: 'medium',
        category: 'meeting',
        actionRequired: 'Prepare meeting materials and agenda',
        impact: 'Medium',
        value: 68000
      }
    ];

    setAlerts(mockAlerts);
  }, []);

  const handleAlertSelect = (alertId) => {
    setSelectedAlerts(prev => 
      prev.includes(alertId) 
        ? prev.filter(id => id !== alertId)
        : [...prev, alertId]
    );
  };

  const handleSelectAll = () => {
    if (selectedAlerts.length === alerts.length) {
      setSelectedAlerts([]);
    } else {
      setSelectedAlerts(alerts.map(alert => alert.id));
    }
  };

  const handleMarkAsRead = (alertId) => {
    setAlerts(prev => 
      prev.map(alert => 
        alert.id === alertId 
          ? { ...alert, isRead: true }
          : alert
      )
    );
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'critical':
        return <Error className="text-[#FF6B6B]" />;
      case 'warning':
        return <Warning className="text-[#FFA726]" />;
      case 'info':
        return <Info className="text-[#B4E50D]" />;
      case 'success':
        return <CheckCircle className="text-[#66BB6A]" />;
      default:
        return <Notifications className="text-[#B0B0B0]" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'border-l-[#FF6B6B] bg-[#2A2A2A]';
      case 'high':
        return 'border-l-[#FFA726] bg-[#1A1A1A]';
      case 'medium':
        return 'border-l-[#B4E50D] bg-[#1A1A1A]';
      case 'low':
        return 'border-l-[#808080] bg-[#1A1A1A]';
      default:
        return 'border-l-[#2A2A2A] bg-[#1A1A1A]';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'portfolio':
        return <Business className="text-[#B4E50D]" />;
      case 'investment':
        return <TrendingUp className="text-[#B4E50D]" />;
      case 'insurance':
        return <Business className="text-[#B4E50D]" />;
      case 'credit':
        return <Person className="text-[#FF6B6B]" />;
      case 'security':
        return <Warning className="text-[#FF6B6B]" />;
      case 'product':
        return <Business className="text-[#B4E50D]" />;
      case 'meeting':
        return <Schedule className="text-[#B4E50D]" />;
      default:
        return <Person className="text-[#B0B0B0]" />;
    }
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'High':
        return 'bg-[#FF6B6B] text-[#FFFFFF]';
      case 'Medium':
        return 'bg-[#FFA726] text-[#0A0A0A]';
      case 'Low':
        return 'bg-[#808080] text-[#FFFFFF]';
      default:
        return 'bg-[#2A2A2A] text-[#B0B0B0]';
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    const matchesSearch = alert.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         alert.customer.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         alert.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === 'all' || alert.category === filter;
    return matchesSearch && matchesFilter;
  });

  const totalPages = Math.ceil(filteredAlerts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAlerts = filteredAlerts.slice(startIndex, endIndex);

  const unreadCount = alerts.filter(alert => !alert.isRead).length;
  const criticalCount = alerts.filter(alert => alert.priority === 'critical').length;
  const totalValue = alerts.reduce((sum, alert) => sum + alert.value, 0);

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-[#FFFFFF]">Alerts & Notifications</h1>
          <p className="text-[#B0B0B0] mt-1">Monitor critical customer alerts and take immediate action</p>
        </div>
        <div className="flex items-center space-x-6">
          <div className="text-center">
            <div className="text-sm text-[#B0B0B0]">Unread Alerts</div>
            <div className="text-2xl font-bold text-[#B4E50D]">{unreadCount}</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-[#B0B0B0]">Critical</div>
            <div className="text-2xl font-bold text-[#FF6B6B]">{criticalCount}</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-[#B0B0B0]">Total Value</div>
            <div className="text-2xl font-bold text-[#B4E50D]">${(totalValue / 1000).toFixed(0)}K</div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-[#1A1A1A] rounded-xl p-6 border border-[#2A2A2A]">
        <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 text-[#B0B0B0]" />
            <input
              type="text"
              placeholder="Search alerts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-[#2A2A2A] border border-[#3A3A3A] rounded-xl text-[#FFFFFF] placeholder-[#B0B0B0] focus:outline-none focus:border-[#B4E50D] focus:ring-2 focus:ring-[#B4E50D] focus:ring-opacity-20 transition-all duration-200"
            />
          </div>
          
          <div className="flex items-center space-x-3">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-3 bg-[#2A2A2A] border border-[#3A3A3A] rounded-xl text-[#FFFFFF] focus:outline-none focus:border-[#B4E50D] focus:ring-2 focus:ring-[#B4E50D] focus:ring-opacity-20 transition-all duration-200"
            >
              <option value="all">All Categories</option>
              <option value="portfolio">Portfolio</option>
              <option value="investment">Investment</option>
              <option value="insurance">Insurance</option>
              <option value="credit">Credit</option>
              <option value="security">Security</option>
              <option value="product">Product</option>
              <option value="meeting">Meeting</option>
            </select>
            
            <button className="p-3 bg-[#2A2A2A] hover:bg-[#3A3A3A] rounded-xl text-[#B0B0B0] hover:text-[#B4E50D] transition-all duration-200">
              <FilterList />
            </button>
          </div>
        </div>
      </div>

      {/* Alerts List */}
      <div className="bg-[#1A1A1A] rounded-xl border border-[#2A2A2A] overflow-hidden">
        {/* Alerts List Header */}
        <div className="bg-[#2A2A2A] px-6 py-4 border-b border-[#3A3A3A]">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <input
                type="checkbox"
                checked={selectedAlerts.length === alerts.length}
                onChange={handleSelectAll}
                className="w-4 h-4 text-[#B4E50D] bg-[#2A2A2A] border-[#3A3A3A] rounded focus:ring-[#B4E50D] focus:ring-2"
              />
              <span className="text-sm text-[#B0B0B0]">
                {selectedAlerts.length > 0 ? `${selectedAlerts.length} selected` : 'Select all'}
              </span>
            </div>
            
            {selectedAlerts.length > 0 && (
              <div className="flex items-center space-x-2">
                <button className="p-2 bg-[#B4E50D] hover:bg-[#B4E50D] rounded-lg text-[#0A0A0A] transition-colors">
                  <MarkEmailRead />
                </button>
                <button className="p-2 bg-[#2A2A2A] hover:bg-[#3A3A3A] rounded-lg text-[#B0B0B0] hover:text-[#FF6B6B] transition-colors">
                  <Delete />
                </button>
                <button className="p-2 bg-[#2A2A2A] hover:bg-[#3A3A3A] rounded-lg text-[#B0B0B0] hover:text-[#B4E50D] transition-colors">
                  <Archive />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Alert Items */}
        <div className="divide-y divide-[#2A2A2A]">
          {currentAlerts.map((alert) => (
            <div 
              key={alert.id}
              className={`px-6 py-4 hover:bg-[#2A2A2A] transition-all duration-200 cursor-pointer border-l-4 ${getPriorityColor(alert.priority)} ${
                !alert.isRead ? 'bg-[#1A1A1A]' : 'bg-[#1A1A1A]'
              }`}
              onClick={() => handleMarkAsRead(alert.id)}
            >
              <div className="flex items-start space-x-4">
                <input
                  type="checkbox"
                  checked={selectedAlerts.includes(alert.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleAlertSelect(alert.id);
                  }}
                  className="w-4 h-4 text-[#B4E50D] bg-[#2A2A2A] border-[#3A3A3A] rounded focus:ring-[#B4E50D] focus:ring-2 mt-1"
                />
                
                <div className="mt-1">
                  {getAlertIcon(alert.type)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        {getCategoryIcon(alert.category)}
                        <span className="text-sm text-[#808080]">{alert.category}</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${getImpactColor(alert.impact)}`}>
                        {alert.impact} Impact
                      </span>
                      <span className="text-sm text-[#B4E50D] font-medium">
                        ${alert.value.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 text-xs text-[#B0B0B0]">
                      <span>{alert.timestamp}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className={`font-medium ${!alert.isRead ? 'text-[#FFFFFF]' : 'text-[#B0B0B0]'}`}>
                          {alert.customer}
                        </span>
                        <span className="text-xs text-[#808080]">({alert.customerSegment})</span>
                      </div>
                      <h3 className={`font-medium mb-2 ${!alert.isRead ? 'text-[#FFFFFF]' : 'text-[#B0B0B0]'}`}>
                        {alert.title}
                      </h3>
                      <p className="text-sm text-[#B0B0B0] mb-2">
                        {alert.description}
                      </p>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-[#B4E50D] font-medium">Action Required:</span>
                        <span className="text-xs text-[#FFFFFF]">{alert.actionRequired}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-1 ml-4">
                      <button className="p-2 hover:bg-[#2A2A2A] rounded-lg text-[#B0B0B0] hover:text-[#B4E50D] transition-colors">
                        <MoreVert />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 bg-[#2A2A2A] border-t border-[#3A3A3A]">
            <div className="flex items-center justify-between">
              <div className="text-sm text-[#B0B0B0]">
                Showing {startIndex + 1}-{Math.min(endIndex, filteredAlerts.length)} of {filteredAlerts.length} alerts
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 bg-[#2A2A2A] text-[#FFFFFF] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#3A3A3A] transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 bg-[#2A2A2A] text-[#FFFFFF] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#3A3A3A] transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertsDashboard;
